import React, { useCallback, useEffect, useState } from 'react';
import { Star, Loader2, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { BookReview, ReviewFilters, reviewsAPI } from '@/services/library-mgmt-api';

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const ReviewsModule: React.FC = () => {
  const [items, setItems] = useState<BookReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvedFilter, setApprovedFilter] = useState<'all' | 'pending' | 'approved'>('pending');
  const [ratingFilter, setRatingFilter] = useState<number | ''>('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const filters: ReviewFilters = { page_size: 100 };
    if (approvedFilter === 'pending') filters.is_approved = false;
    else if (approvedFilter === 'approved') filters.is_approved = true;
    if (ratingFilter) filters.rating = ratingFilter;
    try {
      const data = await reviewsAPI.list(filters);
      setItems(data.results);
    } finally {
      setLoading(false);
    }
  }, [approvedFilter, ratingFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const setApproval = async (r: BookReview, approve: boolean) => {
    setBusyId(r.id);
    try {
      const updated = approve ? await reviewsAPI.approve(r.id) : await reviewsAPI.unapprove(r.id);
      toast.success(approve ? 'Review approved' : 'Review unapproved');
      setItems((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch { /* interceptor */ } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: BookReview) => {
    if (!confirm(`Delete review by ${r.user_name}?`)) return;
    setBusyId(r.id);
    try {
      await reviewsAPI.remove(r.id);
      toast.success('Review deleted');
      setItems((prev) => prev.filter((x) => x.id !== r.id));
    } catch { /* interceptor */ } finally {
      setBusyId(null);
    }
  };

  const pendingCount = items.filter((r) => !r.is_approved).length;

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)] flex items-center gap-2">
            <Star className="h-6 w-6 text-[var(--lib-gold)]" />
            Reviews
          </h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Approve or reject member-submitted book reviews.</p>
        </div>
      </div>

      {/* KPI */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-[var(--lib-hairline)]">
          <Kpi label="Showing" value={items.length.toLocaleString()} />
          <Kpi label="Pending" value={pendingCount.toLocaleString()} tone={pendingCount > 0 ? 'amber' : 'ok'} />
          <Kpi
            label="Avg rating"
            value={items.length ? (items.reduce((s, r) => s + r.rating, 0) / items.length).toFixed(2) : '—'}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {(['pending', 'approved', 'all'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setApprovedFilter(v)}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                approvedFilter === v ? 'bg-[var(--lib-gold)] text-[var(--lib-on-gold)]' : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] bg-[var(--lib-surface)] border border-[var(--lib-hairline)]'
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value ? Number(e.target.value) : '')}
          className="text-[13px] h-9 px-3 rounded-md bg-transparent border border-[var(--lib-hairline)] text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)]"
        >
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>{r} star{r === 1 ? '' : 's'}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg py-16 flex items-center justify-center text-[var(--lib-ink-muted)] text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg py-16 text-center text-sm text-[var(--lib-ink-muted)]">
          No reviews to show.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Stars rating={r.rating} />
                    <span className="text-sm font-medium text-[var(--lib-ink)]">{r.book_title}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">
                      {fmtDate(r.created_at)}
                    </span>
                    {r.is_approved ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border bg-[var(--lib-success)]/10 text-[var(--lib-success)] border-[var(--lib-success)]/30">
                        <CheckCircle2 className="h-3 w-3" />
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] border-[var(--lib-gold)]/30">
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-[var(--lib-ink-muted)]">
                    by <span className="font-medium text-[var(--lib-ink)]">{r.user_name}</span>
                    {r.approved_by_name && r.is_approved && (
                      <span className="text-[var(--lib-ink-muted)]"> · approved by {r.approved_by_name}</span>
                    )}
                  </div>
                  {r.review_text && (
                    <p className="mt-3 text-sm text-[var(--lib-ink-muted)] leading-relaxed">{r.review_text}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {r.is_approved ? (
                    <button
                      onClick={() => void setApproval(r, false)}
                      disabled={busyId === r.id}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[var(--lib-gold)] text-[var(--lib-navy)] hover:brightness-105 transition-colors disabled:opacity-60"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Unapprove
                    </button>
                  ) : (
                    <button
                      onClick={() => void setApproval(r, true)}
                      disabled={busyId === r.id}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[var(--lib-success)] text-white hover:brightness-110 transition-colors disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Approve
                    </button>
                  )}
                  <button
                    onClick={() => void remove(r)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1 text-xs text-[var(--lib-danger)] hover:brightness-105 font-medium"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i <= rating ? 'fill-[var(--lib-gold)] text-[var(--lib-gold)]' : 'text-[var(--lib-hairline)]'}`}
      />
    ))}
  </div>
);

const Kpi: React.FC<{ label: string; value: string; tone?: 'ok' | 'amber' }> = ({ label, value, tone }) => (
  <div className="px-6 py-4">
    <div className={`font-display text-[28px] font-bold ${tone === 'amber' ? 'text-[var(--lib-gold-text)]' : 'text-[var(--lib-ink)]'}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">{label}</div>
  </div>
);

export default ReviewsModule;
